import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Star, ChevronLeft, ChevronRight, Quote, MapPin, Calendar, User } from "lucide-react";
import Navbar from "@/components/common/Navbar";
import Footer from "@/components/home/Footer";

interface Testimonial {
  id: number;
  name: string;
  location: string;
  tour: string;
  date: string;
  content: string;
  rating: number;
  image: string;
  tourImage: string;
}

const Testimonials: React.FC = () => {
  const [activeTestimonial, setActiveTestimonial] = useState<number>(1);
  
  const testimonials: Testimonial[] = [
    {
      id: 1,
      name: "Sarah & David Wilson",
      location: "United States",
      tour: "Namibia Highlights Safari",
      date: "April 2024",
      content: "Our safari with Professor Rion was truly life-changing. His vast knowledge of wildlife and conservation made every moment educational and exciting. The small group size allowed for personalized attention and we were able to get amazingly close to elephants, giraffes and even a pride of lions! The accommodations exceeded our expectations, and the food was delicious. We've already recommended this tour to all our friends!",
      rating: 5,
      image: "/Home/review01.jpeg",
      tourImage: "/Home/NambiaHighlights.jpeg"
    },
    {
      id: 2,
      name: "Hans & Greta Müller",
      location: "Germany",
      tour: "Big Five Safari",
      date: "September 2023",
      content: "Als deutsche Reisende schätzten wir Rions Deutschkenntnisse sehr. Seine Expertise über die Tierwelt und die lokale Kultur vermittelte uns Einblicke, die wir sonst nicht bekommen hätten. Die Unterkünfte waren ausgezeichnet und die Tierbeobachtungen übertrafen unsere Erwartungen. Wir haben alle Big Five gesehen! Besonders gefiel uns die kleine Gruppengröße, die individuelle Aufmerksamkeit und Flexibilität ermöglichte.",
      rating: 5,
      image: "/Home/review01.jpeg",
      tourImage: "/Home/Bigfivee.jpeg"
    },
    {
      id: 3,
      name: "Michael Johnson",
      location: "Canada",
      tour: "Namwild Safari",
      date: "January 2024",
      content: "As a wildlife photographer, I needed a guide who could position me for the best shots, and Rion delivered beyond my expectations. His tracking skills are exceptional, and he knew exactly where to position the vehicle for optimal lighting. I captured some of my best wildlife photos ever, especially the leopard in a tree at sunset. The Namwild Safari is perfect for photography enthusiasts!",
      rating: 5,
      image: "/Home/review01.jpeg",
      tourImage: "/Home/Namwildsafari.jpeg"
    },
    {
      id: 4,
      name: "Emma & James Taylor",
      location: "Australia",
      tour: "Namibia, Botswana & Victoria Falls",
      date: "October 2023",
      content: "The multi-country safari was the trip of a lifetime! Rion's extensive knowledge of the different ecosystems made for an incredible learning experience. The Victoria Falls portion was breathtaking, and the Chobe River cruise gave us amazing views of elephants and hippos. Every day brought new adventures and spectacular wildlife sightings. Worth every penny!",
      rating: 5,
      image: "/Home/review01.jpeg",
      tourImage: "/Home/Victoria.jpeg"
    },
    {
      id: 5,
      name: "Sophia & Thomas Schmidt",
      location: "Switzerland",
      tour: "Walvisbay Excursions",
      date: "March 2024",
      content: "Even though we only had a day for the Walvisbay Excursion, Rion made it unforgettable. His expertise in desert ecosystems and photography tips helped us capture the stunning landscapes of the pink lakes and massive sand dunes. The ancient Welwitschia plants were fascinating, and Rion's explanation of their unique adaptation to the desert environment was eye-opening.",
      rating: 5,
      image: "/Home/review01.jpeg",
      tourImage: "/Home/WalvisExcursions.jpeg"
    },
  ];
  
  const handleNext = () => {
    setActiveTestimonial(prev => (prev === testimonials.length ? 1 : prev + 1));
  };
  
  const handlePrev = () => {
    setActiveTestimonial(prev => (prev === 1 ? testimonials.length : prev - 1));
  };
  
  return (
    <div className="bg-white flex flex-col overflow-hidden">
      {/* Hero Section */}
      <div className="relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('/Tours/safaridrive.jpg')`, 
            height: "500px"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-transparent pointer-events-none"></div>
        </div>
        <div className="relative">
          <Navbar />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-32 flex flex-col items-center justify-center text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white max-w-4xl leading-tight font-display animate-fade-up">Real Guest Experiences</h1>
            <p className="text-xl text-white/90 max-w-2xl mb-8 animate-fade-up animation-delay-200">
              Authentic stories from travelers who have journeyed through Namibia with Professor Rion
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 w-full h-16 bg-gradient-to-t from-white to-transparent"></div>
      </div>
      
      {/* Featured Section */}
      <section className="py-20 bg-white relative z-10">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-6">
              <Quote className="h-8 w-8 text-amber-700" />
            </div>
            <h2 className="text-4xl font-display font-bold text-gray-900 mb-6">
              Hear From Our Guests
            </h2>
            <div className="w-24 h-1 bg-amber-700 mx-auto rounded-full mb-8"></div>
            <p className="text-gray-700 text-lg">
              These are real experiences from guests who have explored the wonders of Namibia, Botswana, and Victoria Falls with Professor Rion Haraeb
            </p>
          </div>
          
          <div className="relative max-w-6xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
            {testimonials.map((testimonial) => (
              <div 
                key={testimonial.id}
                className={`transition-opacity duration-500 ${testimonial.id === activeTestimonial ? 'opacity-100 z-10' : 'opacity-0 absolute inset-0 z-0'}`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-12">
                  <div className="lg:col-span-7 p-8 lg:p-12">
                    <div className="flex justify-between items-start mb-6">
                      <div>
                        <h3 className="text-2xl font-display font-bold text-gray-900 mb-1">
                          {testimonial.name}
                        </h3>
                        <div className="flex items-center text-gray-700">
                          <MapPin className="h-4 w-4 mr-1" />
                          <span>{testimonial.location}</span>
                        </div>
                      </div>
                      <div className="flex">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="h-5 w-5 text-amber-500 fill-amber-500" />
                        ))}
                      </div>
                    </div>
                    
                    <div className="mb-8 relative">
                      <div className="absolute -top-2 -left-3 text-amber-200">
                        <Quote className="h-8 w-8" />
                      </div>
                      <p className="text-gray-700 text-lg italic pl-5 leading-relaxed">
                        {testimonial.content}
                      </p>
                    </div>
                    
                    <div className="flex items-center border-t border-gray-100 pt-6">
                      <div className="flex-1">
                        <div className="flex items-center mb-1">
                          <Calendar className="h-4 w-4 text-amber-700 mr-2" />
                          <span className="text-gray-500 text-sm">{testimonial.date}</span>
                        </div>
                        <p className="text-gray-900 font-medium">{testimonial.tour}</p>
                      </div>
                      
                      <div className="flex space-x-3">
                        <button 
                          onClick={handlePrev}
                          className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center hover:bg-amber-200 transition-colors"
                          aria-label="Previous testimonial"
                        >
                          <ChevronLeft className="h-5 w-5 text-amber-700" />
                        </button>
                        <button 
                          onClick={handleNext}
                          className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center hover:bg-amber-200 transition-colors"
                          aria-label="Next testimonial"
                        >
                          <ChevronRight className="h-5 w-5 text-amber-700" />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="lg:col-span-5 h-64 lg:h-auto relative">
                    <img 
                      src={testimonial.tourImage} 
                      alt={testimonial.tour}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <span className="text-sm font-medium bg-amber-700/80 py-1 px-3 rounded-full">
                        {testimonial.tour}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
              {testimonials.map((testimonial) => (
                <button
                  key={testimonial.id}
                  onClick={() => setActiveTestimonial(testimonial.id)}
                  className={`w-2.5 h-2.5 rounded-full transition-colors ${testimonial.id === activeTestimonial ? 'bg-amber-700' : 'bg-amber-200'}`}
                  aria-label={`Go to testimonial ${testimonial.id}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* Grid of Testimonial Cards */}
      <section className="py-20 bg-amber-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-display font-bold text-gray-900 mb-6">
              More Guest Stories
            </h2>
            <div className="w-24 h-1 bg-amber-700 mx-auto rounded-full mb-8"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: "James & Mary Thompson",
                location: "United Kingdom",
                content: "Etosha National Park was the highlight of our trip. We saw elephants, lions, and even a rare black rhino at a waterhole! Professor Rion's expertise made the safari truly special.",
                tour: "Namwild Safari",
                image: "/Tours/etosha.jpg"
              },
              {
                name: "Lisa Becker",
                location: "Austria",
                content: "Die Dünen von Sossusvlei bei Sonnenaufgang zu erleben war atemberaubend. Professor Rion wusste genau, wann und wo wir sein mussten, um das perfekte Licht für Fotos zu haben.",
                tour: "Namibia Highlights",
                image: "/Home/sossuvlei.jpeg"
              },
              {
                name: "Carlos & Maria Rodriguez",
                location: "Spain",
                content: "Seeing the Victoria Falls was breathtaking! The tour was perfectly organized and Professor Rion's knowledge about the geology and history of the falls added so much to the experience.",
                tour: "Namibia, Botswana & Victoria Falls",
                image: "/Home/Victoriafall.jpeg"
              },
              {
                name: "John & Karen Miller",
                location: "Canada",
                content: "The desert-adapted elephants in Damaraland were incredible. Professor Rion knew exactly where to find them and taught us so much about their unique adaptations to survive in such harsh conditions.",
                tour: "Namibia Highlights Safari",
                image: "/Home/damaraland.jpg"
              },
              {
                name: "Thomas Schneider",
                location: "Switzerland",
                content: "Die Big Five an einem Tag zu sehen war ein unvergessliches Erlebnis. Besonders der Leopard im Baum war ein magischer Moment, den Professor Rion mit seinen Tracking-Fähigkeiten ermöglicht hat.",
                tour: "Big Five Safari",
                image: "/Tours/Leopard.jpg"
              },
              {
                name: "Robert & Amanda Wilson",
                location: "Australia",
                content: "The Walvis Bay excursion was a perfect day trip. The pink salt lakes were stunning for photography, and seeing the ancient Welwitschia plants was fascinating.",
                tour: "Walvisbay Excursions",
                image: "/Tours/Lagoon.jpg"
              },
            ].map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl shadow-md overflow-hidden flex flex-col h-full transition-transform hover:shadow-lg hover:-translate-y-1"
              >
                <div className="h-48 overflow-hidden">
                  <img 
                    src={testimonial.image}
                    alt={testimonial.tour}
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <div className="p-6 flex flex-col flex-grow">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-display font-bold text-gray-900 mb-1">
                        {testimonial.name}
                      </h3>
                      <div className="flex items-center text-gray-600 text-sm">
                        <MapPin className="h-3 w-3 mr-1" />
                        <span>{testimonial.location}</span>
                      </div>
                    </div>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 text-amber-500 fill-amber-500" />
                      ))}
                    </div>
                  </div>
                  
                  <p className="text-gray-700 italic mb-4 flex-grow">
                    "{testimonial.content}"
                  </p>
                  
                  <div className="pt-4 border-t border-gray-100 text-amber-700 font-medium">
                    {testimonial.tour}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-cover bg-center relative" style={{ backgroundImage: "linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url('/Tours/girrafes.jpg')" }}>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-display font-bold text-white mb-6">
              Begin Your African Adventure
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Join Professor Rion Haraeb for an unforgettable safari experience that will leave you with memories to last a lifetime
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/tours"
                className="px-8 py-4 bg-amber-700 text-white font-medium rounded-lg hover:bg-amber-800 transition-colors"
              >
                Explore Our Safaris
              </Link>
              <Link 
                to="/contact"
                className="px-8 py-4 border-2 border-white text-white font-medium rounded-lg hover:bg-white/10 transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Testimonials; 